import { ICS2InformationExchangeMessages } from 'store/declarations/eu/ics2/ICS2InformationExchangeMessages';
import {
    ICS2Notification,
    ICS2NotificationError,
    ICS2NotificationProcessingStatus,
} from 'store/declarations/eu/ics2/ics2-notification';

/** The only two ICS2 messages that carry an `error` array. */
export const ICS2_ERROR_MESSAGE_TYPES: string[] = [
    ICS2InformationExchangeMessages.IE3N01,
    ICS2InformationExchangeMessages.IE3N99,
];

/**
 * `processingStatus` is only ever present on ICS2 notifications, so it tells an ICS2 notification
 * apart from every other system's inside the shared notification helpers.
 */
export const isIcs2Notification = (notification: any): notification is ICS2Notification =>
    Boolean(notification) && 'processingStatus' in notification;

/** A notification the backend has set aside - a newer submission overtook the one it answers. */
export const isSupersededIcs2Notification = (notification: any) =>
    isIcs2Notification(notification) && notification.processingStatus === ICS2NotificationProcessingStatus.SUPERSEDED;

export const getIcs2NotificationErrors = (notification: any): ICS2NotificationError[] | undefined =>
    (notification as ICS2Notification | undefined)?.notification?.error ?? undefined;

/**
 * Referral requests, kept out of the drawer entirely: the Customs Requests tab owns them.
 *
 * The drawer surfaces only the newest message, while one IE3Q02/IE3Q03 carries up to 999 referrals
 * and further requests arrive while earlier ones are still open - so it could only ever show a
 * fraction of what is outstanding. The tab's badge counts all of them.
 */
export const ICS2_CUSTOMS_REQUEST_MESSAGE_TYPES: string[] = [
    ICS2InformationExchangeMessages.IE3Q02,
    ICS2InformationExchangeMessages.IE3Q03,
];

export const isIcs2CustomsRequestNotification = (notification: any): boolean =>
    isIcs2Notification(notification) && ICS2_CUSTOMS_REQUEST_MESSAGE_TYPES.includes(notification.messageType ?? '');

/**
 * The ICS2 messages that ask the trader to do something *in the drawer*. The backend never sets
 * `Declaration.actionRequired` for ICS2 - every call site of it is Ireland AES or NCTS - so the
 * drawer derives action-required from the message type instead.
 *
 * IE3Q02/IE3Q03 are deliberately absent; see `ICS2_CUSTOMS_REQUEST_MESSAGE_TYPES`.
 */
export const ICS2_ACTION_REQUIRED_MESSAGE_TYPES: string[] = [
    ICS2InformationExchangeMessages.IE3Q01,
    ICS2InformationExchangeMessages.IE3R08,
];

export const isIcs2ActionRequiredNotification = (notification: any): boolean =>
    isIcs2Notification(notification) &&
    !isSupersededIcs2Notification(notification) &&
    ICS2_ACTION_REQUIRED_MESSAGE_TYPES.includes(notification.messageType ?? '');

/** Oldest first, matching the order `extractNotificationsData` sorts notifications in. */
export const getIcs2ActionRequiredNotifications = (notifications: any[] | undefined): ICS2Notification[] =>
    (notifications ?? []).filter(isIcs2ActionRequiredNotification);

/**
 * The message the drawer should open on. This is deliberately not simply the newest notification:
 * an unanswered IE3Q01 still needs an answer after, say, an IE3N03 assessment notification has
 * arrived on top of it.
 */
export const getLatestIcs2ActionRequiredNotification = (
    notifications: any[] | undefined
): ICS2Notification | undefined => {
    const actionRequired = getIcs2ActionRequiredNotifications(notifications);
    return actionRequired[actionRequired.length - 1];
};
